import { useEffect } from 'react'

import { useMutation } from '@apollo/client'
import { AirportProvider } from '@place/ui/contexts'
import { ReviewCard } from '@place/ui/presets'

import { gql } from '@web/gql'
import { ReviewContent_ReviewGroupFragment } from '@web/gql/graphql'
import { ContentProvider } from '@web/libs/contexts/ContentContext'
import { langVar } from '@web/libs/translate/translate.state'
import { ReviewEndArea, useReviewCardPropsForReviewEnd } from '@web/pages/review/components/ReviewDetail/useReviewCard'

import styles from './ReviewContent.module.scss'

const mutation = gql(`
  mutation ReviewContent_IncreaseReviewGroupsView($IncreaseReviewGroupsViewInput: IncreaseReviewGroupsViewInput!) {
    increaseReviewGroupsView(input: $IncreaseReviewGroupsViewInput) {
      __typename
    }
  }
`)

interface Props {
  reviewGroup: ReviewContent_ReviewGroupFragment
}

/**
 * [리뷰 유형 케이스 위키](https://oss.navercorp.com/place/local/wiki/MY%ED%94%8C%EB%A0%88%EC%9D%B4%EC%8A%A4-%EB%A6%AC%EB%B7%B0-%EB%AF%B8%EB%85%B8%EC%B6%9C-&-%EA%B2%8C%EC%8B%9C%EC%A4%91%EB%8B%A8-%EC%8A%A4%ED%8E%99-%EC%A0%95%EB%A6%AC) \
 * 리뷰 유형에 따라 노출되거나 미노출되는 컴포넌트들이 있습니다.
 */
const ReviewContent = ({ reviewGroup }: Props) => {
  const lang = langVar()
  const [increaseReviewGroupsView] = useMutation(mutation)

  useEffect(() => {
    if (!reviewGroup) return
    increaseReviewGroupsView({
      variables: {
        IncreaseReviewGroupsViewInput: {
          ids: [reviewGroup.id],
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewGroup.id])

  const reviewCardProps = useReviewCardPropsForReviewEnd(reviewGroup, ReviewEndArea.MAIN)

  return (
    <ContentProvider id={reviewGroup.id} type={reviewGroup.__typename}>
      <AirportProvider locale={lang}>
        <ReviewCard as="div" className={styles.list_item} {...reviewCardProps} />
      </AirportProvider>
    </ContentProvider>
  )
}

ReviewContent.fragments = {
  reviewGroup: gql(`
    fragment ReviewContent_ReviewGroup on ReviewGroup {
      id
      rating
      author {
        id
      }
      place {
        id
        categoryIdPath
        type
      }
      exposeStatus
      reply {
        isQualified
      }
      ...OwnerReply_ReviewGroup
      ...ReviewStatusNotification_ReviewGroup
      ...ReviewProfile_ReviewGroup
      ...ReviewDescription_ReviewGroup
      ...MediaList_ReviewGroup
    }
  `),
  profile: gql(`
    fragment ReviewProfile_ReviewGroup on ReviewGroup {
      author {
        id
        ...FollowButton_User
        ...DescHeader_User
      }
      visit {
        id
        visitedDateTime
      }
      ...ContextMenuReview_ReviewGroup
    }
  `),
  description: gql(`
    fragment ReviewDescription_ReviewGroup on ReviewGroup {
      author {
        id
      }
      mediaItems {
        id
      }
      text
      exposeStatus
      ...TextReview_ReviewGroup
      ...VotedKeywords_ReviewGroup
      ...MoreReviewContent_ReviewGroup
      ...ReviewStat_ReviewGroup
      ...VisitKeywords_ReviewGroup
    }
  `),
}

export default ReviewContent
